import { useSelector } from 'react-redux';
import React, { useEffect, useState } from 'react';

import { Box, Grid } from '@mui/material';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import { ReactComponent as EditIcon } from '../../../../assets/icons/Edit.svg';
import { ReactComponent as EditIconDarkMode } from '../../../../assets/icons/EditDarkMode.svg';

import { useThemeToggle } from 'hooks/useThemeToggle';
import { DataTable, Menu, TablePagination, Typography } from 'shared';
import { paymentPlanStylesTable } from 'components/Inventory/style/style';
import { usePaymentPlanService } from 'store/services/paymentPlan.service';
import { paymentPlanTableHeaders } from 'components/Inventory/utils/paymentPlanTableHeaders';

const PaymentPlansTable = ({
    page,
    onEdit = () => {},
    data,
    loading,
    handlePageChange,
    isSelectable = false,
    selectedRows = [],
    onSelectionChange = () => {},
    allowActions = false,
}) => {
    const classes = paymentPlanStylesTable();

    const { themeMode } = useThemeToggle();
    const [selectedRowId, setSelectedRowId] = useState(null);
    const [menuSelectedRow, setMenuSelectedRow] = useState(null);
    const { deletePaymentPlan } = usePaymentPlanService();
    const companyData = useSelector((state) => state.company.data);

    const [anchorEl, setAnchorEl] = useState(null);

    const handleSetAnchorEl = (event, row) => {
        setAnchorEl(event.currentTarget);
        setMenuSelectedRow(row);
    };

    const handleCloseMenu = () => {
        setAnchorEl(null);
    };

    const handleRowSelection = (newSelection) => {
        const newSelectedRowId = Object.keys(newSelection)[0];
        if (newSelectedRowId === selectedRowId) {
            setSelectedRowId(null);
        } else {
            setSelectedRowId(newSelectedRowId);
        }
    };

    useEffect(() => {
        if (data?.results) {
            const selectedRowData = data.results.find((row) => row.id == selectedRowId);
            if (selectedRowData) {
                onSelectionChange([selectedRowData]);
            } else {
                onSelectionChange([]);
            }
        }
    }, [selectedRowId, data?.results]);

    useEffect(() => {
        if (selectedRows.length === 0 && selectedRowId !== null) {
            setSelectedRowId(null);
        } else if (selectedRows.length === 1 && selectedRows[0]?.id !== selectedRowId) {
            setSelectedRowId(selectedRows[0].id);
        }
    }, [selectedRows]);

    return (
        <Grid container>
            <Grid item xs={12}>
                <DataTable
                    isSelectable={isSelectable}
                    isLoading={loading}
                    data={data?.results}
                    headCells={paymentPlanTableHeaders.filter((cell) => cell.id !== 'select')}
                    selected={selectedRowId ? { [selectedRowId]: true } : {}}
                    setSelected={handleRowSelection}
                    showHeaderCheckBox={false}
                    canEdit={allowActions ? true : false}
                    setAnchorEl={handleSetAnchorEl}
                />
            </Grid>

            <Menu
                items={[
                    {
                        name: (
                            <Box display={'flex'} alignItems={'center'}>
                                {themeMode === 'dark' ? <EditIconDarkMode /> : <EditIcon />}
                                &nbsp;&nbsp;
                                <Typography text="Edit" sx={classes.editText} />
                            </Box>
                        ),
                        onClick: () => {
                            if (menuSelectedRow?.id) onEdit(menuSelectedRow.id, menuSelectedRow);
                            handleCloseMenu();
                        },
                    },
                    {
                        name: (
                            <Box display={'flex'} alignItems={'center'}>
                                <DeleteOutlineIcon color="error" />
                                &nbsp;&nbsp;
                                <Typography text="Delete" sx={classes.deleteText} />
                            </Box>
                        ),
                        onClick: async () => {
                            handleCloseMenu();

                            if (!menuSelectedRow?.id) return;

                            try {
                                await deletePaymentPlan(companyData?.id, menuSelectedRow.id);

                                handlePageChange(page);
                            } catch (error) {
                                console.error('Failed to delete payment plan', error);
                            }
                        },
                    },
                ]}
                anchorEl={anchorEl}
                handleClose={handleCloseMenu}
            />

            <Grid item xs={12} p={'10px 0'}>
                <TablePagination
                    page={page}
                    count={data?.count}
                    onChange={(e, pageNumber) => handlePageChange(pageNumber)}
                />
            </Grid>
        </Grid>
    );
};

export default PaymentPlansTable;
